import { RcFile } from "antd/es/upload"
import Modules from "application/modules"
import { ApplicationState, Dispatch } from "application/redux"
import { isSU } from "contexts/ACL/service"
import { add, remove } from 'core/spinner/redux/actions'
import { addToaster } from "core/toaster/redux/actions"
import { ToasterType } from "core/toaster/redux/types"
import { getConfigManagementUploadLogs } from "../logs/configManagement/actions"
import { filesMapper, getFileNameFromContentDisposition, saveFile } from "./helper"
import { deleteConfigurationFile, downloadConfigurationFile, fetchConfigurationFiles, uploadConfigurationFile } from "./services"
import { File, RTF_CONFIGURATION_MANAGEMENT_DATA_FETCH, RTF_CONFIGURATION_MANAGEMENT_FILE_DELETE, RTF_CONFIGURATION_MANAGEMENT_FILE_DOWNLOAD, RTF_CONFIGURATION_MANAGEMENT_FILE_UPLOAD, RTFConfigurationManagementActions } from "./types"

function getDataSuccess(payload: Array<File>) {
  return {
    type: RTFConfigurationManagementActions.setFiles,
    payload,
  }
}

function setEmptyState() {
  return {
    type: RTFConfigurationManagementActions.clearConfigManagementState,
  }
}

export function getConfigManagementData() {
  return async function (dispatch: Dispatch, getState: () => ApplicationState) {
    try {
      dispatch(add(RTF_CONFIGURATION_MANAGEMENT_DATA_FETCH, {}))
      const { data } = await fetchConfigurationFiles()
      dispatch(getDataSuccess(filesMapper(data)))
    } catch (error) {
      dispatch(addToaster({
        title: 'rtf.configuration_management',
        message: 'error_messages.VIPOC_ERROR_071',
        type: ToasterType.danger,
      }))
      dispatch(setEmptyState())
    } finally {
      dispatch(remove(RTF_CONFIGURATION_MANAGEMENT_DATA_FETCH))
    }
  }
}

// an upload or a delete also creates an audit entry, so both lists are refreshed
// the audit log is SU-only, so OU users only refresh the files
function refreshConfigManagement() {
  return async function (dispatch: Dispatch, getState: () => ApplicationState) {
    await dispatch(getConfigManagementData())
    if (!isSU(Modules.rtf)) return

    const { filter, pageSize } = getState().rtf.rtfLogs.configManagement
    dispatch(getConfigManagementUploadLogs(filter, 1, pageSize))
  }
}

export function downloadConfigManagementFile(file: File) {
  return async function(dispatch: Dispatch, getState: () => ApplicationState) {
    try {
      dispatch(add(RTF_CONFIGURATION_MANAGEMENT_FILE_DOWNLOAD, {}));
      const { data, headers } = await downloadConfigurationFile(file.fileName)
      saveFile(data, getFileNameFromContentDisposition(headers['content-disposition'], file.fileName))
    } catch (error) {
      dispatch(
        addToaster({
          title: 'rtf.configuration_management',
          message: 'error_messages.VIPOC_ERROR_003',
          type: ToasterType.danger,
        })
      )
    } finally {
      dispatch(remove(RTF_CONFIGURATION_MANAGEMENT_FILE_DOWNLOAD))
    }
  }
}

export function uploadFile(file: RcFile) {
  return async function(dispatch: Dispatch, getState: () => ApplicationState) {
    try {
      dispatch(add(RTF_CONFIGURATION_MANAGEMENT_FILE_UPLOAD, {}))
      await uploadConfigurationFile(file)
      dispatch(addToaster({
        title: 'rtf.configuration_management',
        message : "rtf.configuration_management_upload_success",
        type: ToasterType.success
      }))
      await dispatch(refreshConfigManagement())
    } catch (error) {
      dispatch(addToaster({
        title: 'rtf.configuration_management',
        message: 'error_messages.VIPOC_ERROR_005',
        type: ToasterType.danger,
      }))
    } finally {
      dispatch(remove(RTF_CONFIGURATION_MANAGEMENT_FILE_UPLOAD))
    }
  }
}

export function deleteConfigManagementFile(file: File) {
  return async function(dispatch: Dispatch, getState: () => ApplicationState) {
    try {
      dispatch(add(RTF_CONFIGURATION_MANAGEMENT_FILE_DELETE, {}))
      await deleteConfigurationFile(file.fileName)
      dispatch(addToaster({
        title: 'rtf.configuration_management',
        message: "rtf.configuration_management_delete_success",
        type: ToasterType.success,
      }))
      await dispatch(refreshConfigManagement())
    } catch (error) {
      dispatch(addToaster({
        title: 'rtf.configuration_management',
        message: 'error_messages.VIPOC_ERROR_005',
        type: ToasterType.danger,
      }))
    } finally {
      dispatch(remove(RTF_CONFIGURATION_MANAGEMENT_FILE_DELETE))
    }
  }
}

export function clearConfigManagementData() {
  return function (dispatch: Dispatch) {
    return  dispatch(setEmptyState())
  }
}

export function invalidFileUploadToaster(title: string, message: string) {
  return async function (dispatch: Dispatch) {
    return dispatch(addToaster({
      title,
      message,
      type: ToasterType.danger,
    }));
  };
}
